/**
 * Shared primitives for the raster tile pixel effects (shaded-relief blend and
 * background / border keying).
 *
 * Both effects need the same chain: fetch a tile, get at its RGBA, edit it, and
 * hand it back to the engine. The first implementation ended that chain with
 * `canvas.toBlob('image/png')`, which is the single most expensive step in the
 * whole pipeline — and unnecessary, because neither engine wants bytes:
 *
 *   - MapLibre's `addProtocol` forwards an `ImageBitmap` response straight to
 *     the texture uploader (`response.data instanceof ImageBitmap` short-circuit
 *     in its image request queue).
 *   - OpenLayers' `ImageTile.setImage` accepts a canvas and marks the tile
 *     loaded.
 *
 * So processed tiles leave here as decoded pixels. Re-encoding only survives as
 * a fallback for environments without `createImageBitmap`.
 */

/**
 * Cap on tiles being decoded / read back at once.
 *
 * `getImageData` is synchronous main-thread work, and a zoom burst can ask for
 * dozens of tiles in the same frame. Holding a few in flight keeps the network
 * saturated without queueing a long run of blocking reads ahead of the next
 * paint.
 */
const TILE_TASK_CONCURRENCY = 6;

export type TileImage = ImageBitmap | HTMLImageElement;

let runningTasks = 0;
const waitingTasks: (() => void)[] = [];

/** Run `task` once fewer than {@link TILE_TASK_CONCURRENCY} peers are active. */
export async function runTileTask<T>(task: () => Promise<T>): Promise<T> {
  if (runningTasks >= TILE_TASK_CONCURRENCY) {
    await new Promise<void>((resolve) => {
      waitingTasks.push(resolve);
    });
  }
  runningTasks += 1;
  try {
    return await task();
  } finally {
    runningTasks -= 1;
    waitingTasks.shift()?.();
  }
}

function abortError(): Error {
  return typeof DOMException === 'function'
    ? new DOMException('Tile post-processing aborted', 'AbortError')
    : new Error('Tile post-processing aborted');
}

/**
 * Bail out of a half-finished tile whose consumer has moved on. MapLibre aborts
 * tiles as soon as they leave the viewport, so checking between the fetch and
 * the pixel work keeps a fast pan from paying for tiles nobody will draw.
 */
export function throwIfAborted(signal?: AbortSignal | null): void {
  if (signal?.aborted) throw abortError();
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load tile: ${url}`));
    img.src = url;
  });
}

/** Decode tile bytes without going through an `<img>` when the browser can. */
export async function decodeTileBlob(blob: Blob): Promise<TileImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob);
    } catch {
      /* fall through to the <img> decode below */
    }
  }
  const objectUrl = URL.createObjectURL(blob);
  try {
    return await loadImageElement(objectUrl);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/** Fetch one tile and decode it. Rejects on a non-2xx response. */
export async function fetchTileImage(
  url: string,
  signal?: AbortSignal | null,
): Promise<TileImage> {
  const res = await fetch(url, signal ? { signal } : undefined);
  if (!res.ok) throw new Error(`Tile HTTP ${res.status}`);
  const blob = await res.blob();
  throwIfAborted(signal);
  return decodeTileBlob(blob);
}

/**
 * One canvas reused for every read-back.
 *
 * Allocating a 256² canvas per tile was a steady source of GC churn while
 * panning. Reuse is safe because {@link tileImageToImageData} never awaits
 * between the draw and the read.
 */
let scratchCanvas: HTMLCanvasElement | null = null;
let scratchContext: CanvasRenderingContext2D | null = null;

function acquireScratchContext(
  width: number,
  height: number,
): CanvasRenderingContext2D {
  if (!scratchCanvas) scratchCanvas = document.createElement('canvas');
  if (scratchCanvas.width !== width || scratchCanvas.height !== height) {
    scratchCanvas.width = width;
    scratchCanvas.height = height;
  }
  if (!scratchContext) {
    scratchContext = scratchCanvas.getContext('2d', {
      willReadFrequently: true,
    });
  }
  if (!scratchContext) {
    throw new Error('2D canvas unavailable for tile post-processing');
  }
  return scratchContext;
}

function imageWidth(img: TileImage): number {
  return 'naturalWidth' in img ? img.naturalWidth || img.width : img.width;
}

function imageHeight(img: TileImage): number {
  return 'naturalHeight' in img ? img.naturalHeight || img.height : img.height;
}

/**
 * Read a decoded tile's RGBA. Synchronous end to end, and closes the source
 * bitmap so its backing store is released before the next tile arrives.
 */
export function tileImageToImageData(img: TileImage): ImageData {
  const width = imageWidth(img);
  const height = imageHeight(img);
  if (!(width > 0) || !(height > 0)) {
    throw new Error('Tile decoded with zero size');
  }
  const ctx = acquireScratchContext(width, height);
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0);
  const data = ctx.getImageData(0, 0, width, height);
  if (typeof ImageBitmap !== 'undefined' && img instanceof ImageBitmap) {
    img.close();
  }
  return data;
}

/** Wrap pixels in a canvas the caller owns — OpenLayers keeps this one. */
export function imageDataToCanvas(data: ImageData): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = data.width;
  canvas.height = data.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable for tile post-processing');
  ctx.putImageData(data, 0, 0);
  return canvas;
}

export function imageDataToPngBlob(data: ImageData): Promise<Blob> {
  const canvas = imageDataToCanvas(data);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('PNG encode failed'))),
      'image/png',
    );
  });
}

/**
 * Package pixels for a MapLibre `addProtocol` response. An `ImageBitmap` is
 * handed to the texture uploader as-is; the PNG `ArrayBuffer` is the fallback
 * for engines / environments that only accept encoded bytes.
 */
export async function imageDataToEngineImage(
  data: ImageData,
): Promise<ImageBitmap | ArrayBuffer> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(data);
    } catch {
      /* fall through to the PNG encode below */
    }
  }
  return (await imageDataToPngBlob(data)).arrayBuffer();
}

/** Substitute `{z}/{x}/{y}` in a tile template. */
export function fillTilePlaceholders(
  template: string,
  z: string,
  x: string,
  y: string,
): string {
  return template
    .replace(/\{z\}/g, z)
    .replace(/\{x\}/g, x)
    .replace(/\{y\}/g, y);
}

/** Read a query param off a tile URL, tolerating un-decodable values. */
export function readTileParam(url: string, key: string): string | null {
  const match = new RegExp(`[?&]${key}=([^&]*)`).exec(url);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}
